import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from '@ibk/shared-translation';
import { DiscountType } from '@ibk/shared-types';
import { Icon } from '@ibk/shared-ui';
import { useValidateRechargeSpreadsheetMutation } from '../../../../services/benefitApi';
import { FileLoadedView } from './components/FileLoadedView';
import { ImportSelectionView } from './components/ImportSelectionView';
import { InstructionsView } from './components/InstructionsView';
import { SuccessView } from './components/SuccessView';
import { UploadErrorView } from './components/UploadErrorView';
import { UploadProgressView } from './components/UploadProgressView';
import { UploadView } from './components/UploadView';
import { ValidationResultView } from './components/ValidationResultView';
import { useSpreadsheetImport } from './hooks/useSpreadsheetImport';
import styles from './SpreadsheetImportModal.module.css';
import { ImportModalView, UploadErrorType } from './types';
import { getModalTitle } from './utils/modalTitle';
import { extractOutcome, mapBackendSuccessToValidatedRows } from './utils/outcomeMapper';
export function SpreadsheetImportModal({ isOpen, onClose, onManualEntry, onImportComplete, voucherContractId, voucherTypeId, releaseType, discountType, }) {
    const { t } = useTranslation();
    const { currentView, file, validatedRows, inlineError, uploadDate, validationErrors, validationErrorSheet, setCurrentView, setUploadErrorType, setInlineError, handleFileSelected, handleImport, resetState, handleResubmit, } = useSpreadsheetImport();
    const [validateRechargeSpreadsheet] = useValidateRechargeSpreadsheetMutation();
    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, resetState]);
    const handleClose = useCallback(() => {
        resetState();
        onClose();
    }, [resetState, onClose]);
    const handleManualEntry = useCallback(() => {
        handleClose();
        onManualEntry();
    }, [handleClose, onManualEntry]);
    const handleSpreadsheetImport = useCallback(() => {
        setCurrentView(ImportModalView.UPLOAD);
    }, [setCurrentView]);
    const handleShowInstructions = useCallback(() => {
        setCurrentView(ImportModalView.INSTRUCTIONS);
    }, [setCurrentView]);
    const handleBackFromInstructions = useCallback(() => {
        setCurrentView(ImportModalView.UPLOAD);
    }, [setCurrentView]);
    const handleUploadError = useCallback((errorType) => {
        if (errorType === UploadErrorType.FILE_SIZE || errorType === UploadErrorType.INVALID_FORMAT) {
            setInlineError(errorType);
            return;
        }
        setUploadErrorType(errorType);
        setCurrentView(ImportModalView.UPLOAD_ERROR);
    }, [setUploadErrorType, setCurrentView, setInlineError]);
    const handleImportClick = useCallback(() => {
        if (!file || !voucherContractId || !voucherTypeId) {
            return;
        }
        const resolvedDiscountType = discountType ?? DiscountType.NO_DISCOUNT;
        handleImport(async () => {
            try {
                const result = await validateRechargeSpreadsheet({
                    voucherContractId,
                    voucherTypeId,
                    releaseType,
                    discountType: resolvedDiscountType,
                    file,
                }).unwrap();
                return {
                    kind: 'SUCCESS',
                    validatedRows: mapBackendSuccessToValidatedRows(result),
                };
            }
            catch (error) {
                return extractOutcome(error);
            }
        });
    }, [file, voucherContractId, voucherTypeId, releaseType, discountType, handleImport, validateRechargeSpreadsheet]);
    const handleAdvance = useCallback(() => {
        onImportComplete(validatedRows);
        handleClose();
    }, [onImportComplete, validatedRows, handleClose]);
    const renderView = () => {
        switch (currentView) {
            case ImportModalView.SELECTION:
                return _jsx(ImportSelectionView, { onManualEntry: handleManualEntry, onSpreadsheetImport: handleSpreadsheetImport });
            case ImportModalView.UPLOAD:
                return (_jsx(UploadView, { onFileSelected: handleFileSelected, onShowInstructions: handleShowInstructions, onUploadError: handleUploadError, inlineError: inlineError }));
            case ImportModalView.INSTRUCTIONS:
                return _jsx(InstructionsView, { onBack: handleBackFromInstructions });
            case ImportModalView.UPLOADING:
                return _jsx(UploadProgressView, { fileName: file?.name ?? '', fileSize: file?.size ?? 0, onCancel: handleResubmit });
            case ImportModalView.LOADING:
                return (_jsxs("div", { className: styles.loadingContainer, "data-testid": 'viewSpreadsheetImportLoading', children: [_jsx("div", { className: styles.loadingBigIconOrange, children: _jsx("div", { className: styles.loadingSpinner }) }), _jsx("p", { className: styles.loadingMessage, children: t('spreadsheet.import.loading.message') })] }));
            case ImportModalView.FILE_LOADED:
                return (_jsx(FileLoadedView, { fileName: file?.name ?? '', fileSize: file?.size ?? 0, uploadDate: uploadDate ?? new Date(), onImport: handleImportClick, onRemoveFile: handleResubmit }));
            case ImportModalView.UPLOAD_ERROR:
                return _jsx(UploadErrorView, { fileName: file?.name, onResubmit: handleResubmit });
            case ImportModalView.VALIDATION_RESULT:
                return (_jsx(ValidationResultView, { errors: validationErrors, fileName: file?.name, uploadDate: uploadDate ?? undefined, onResubmit: handleResubmit, errorSheetBase64: validationErrorSheet ?? undefined }));
            case ImportModalView.SUCCESS:
                return _jsx(SuccessView, { fileName: file?.name ?? '', onResubmit: handleResubmit, onAdvance: handleAdvance });
            default:
                return null;
        }
    };
    return (_jsxs(Modal, { show: isOpen, onHide: handleClose, centered: true, backdrop: 'static', className: styles.modal, "data-testid": 'modalSpreadsheetImport', children: [_jsxs("div", { className: styles.modalHeader, children: [_jsx("h5", { className: styles.modalTitle, children: getModalTitle(currentView, t) }), _jsx("button", { type: 'button', className: styles.closeButton, onClick: handleClose, "data-testid": 'buttonSpreadsheetImportClose', children: _jsx(Icon, { name: 'actionClose', variant: 'default', width: 16, height: 16 }) })] }), _jsx(Modal.Body, { className: styles.modalBody, children: renderView() })] }));
}
